/*General helper functions for sending requests to flightdeck webservice and
  formatting data */
import moment from 'moment';
import {
  calculateReqFuel,
  decodeBriefingBody,
  filterFlights,
  isTrainingMode,
} from '../helpers';
import { AVIATIONWEATHER_ERROR } from './enums';

// Library for parsing XML
const { parseString, parseStringPromise } = require('xml2js');

export const fetchUserAuthorization = async (employeeNumber) =>
  await fetch(process.env.REACT_APP_SERVER_URL + '/checksameuser', {
    credentials: 'include',
    method: 'POST',
    body: JSON.stringify({ empno: employeeNumber }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

export const fetchPassengerList = async (flightData) => {
  const {
    flightPrefix,
    flightNumber,
    flightDate,
    airport,
    flightSTD,
    flightSTA,
    finalDestination,
  } = flightData;

  const response = await fetch(
    process.env.REACT_APP_SERVER_URL + `/getPassengerList`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        flightPrefix,
        flightNumber,
        flightDate,
        airport,
        flightSTD,
        flightSTA,
        finalDestination,
      }),
    }
  );

  if (response.status === 400) {
    const errorData = await response.json();
    if (errorData.dataUnavailableInAmadeus) {
      return {
        success: false,
        data: [],
        dataUnavailableInAmadeus: true,
        message: errorData.message,
      };
    }
  }

  if (!response.ok) {
    console.error(response);
    return {
      success: false,
      data: [],
      message: 'Failed to fetch passenger list',
    };
  }

  const { data } = await response.json();
  return {
    success: true,
    data,
  };
};

export const fetchSeatmap = async (flightData) => {
  const { flightPrefix, flightNumber, flightDate, boardPoint, offPoint } =
    flightData;

  const response = await fetch(
    process.env.REACT_APP_SERVER_URL + `/getSeatmap`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        flightPrefix,
        flightNumber,
        flightDate,
        boardPoint,
        offPoint,
      }),
    }
  );

  if (response.status === 400) {
    const errorData = await response.json();
    if (errorData.dataUnavailableInAmadeus) {
      return {
        success: false,
        data: [],
        dataUnavailableInAmadeus: true,
        message: errorData.message,
      };
    }
  }

  if (!response.ok) {
    console.error(response);
    return {
      success: false,
      data: [],
      message: 'Failed to fetch seatmap',
    };
  }

  const { seatingDetails } = await response.json();
  return {
    success: true,
    data: seatingDetails,
  };
};

export const fetchBagList = async (flightData) => {
  const { flightPrefix, flightNumber, flightDate, airport } = flightData;

  const formattedDate = flightDate.includes('-') ? flightDate : flightDate;

  const response = await fetch(
    process.env.REACT_APP_SERVER_URL +
    `/getBagList?flightPrefix=${flightPrefix}&flightNumber=${flightNumber}&flightDate=${formattedDate}&airport=${airport}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    return {
      success: false,
      data: null,
      message: 'Failed to fetch baggage list',
    };
  }

  const data = await response.json();

  return { success: true, data };
};

export const fetchColdSoak = async (flightData) => {
  const { sACReg, sDepIATA, sDestIATA, sFltNo, sDate } = flightData;
  let coldSoak = null;

  try {
    const res = await fetch(process.env.REACT_APP_SERVER_URL + '/getColdSoak', {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sACReg,
        sDepIATA,
        sDestIATA,
        sFltNo,
        sDate,
      }),
    });

    if (!res.ok) {
      console.error('Error getting cold soak', response);
    }

    const response = await res.json();

    parseString(response, async function (err, result) {
      const dataResult =
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.GetColdSoakResponse?.[
          '0'
        ]?.GetColdSoakResult?.['0'];
      if (dataResult) {
        coldSoak = dataResult;
      }
    });
  } catch (err) {
    console.error(err);
  }

  return coldSoak;
};

export const fetchFlts = async (simulator = false) => {
  const url = simulator
    ? process.env.REACT_APP_SERVER_URL + '/getFlts?simulator=true'
    : process.env.REACT_APP_SERVER_URL + '/getFlts';
  return await fetch(url, {
    credentials: 'include',
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const fetchFlt = async (sFltNo, sDep, sDest, sDate, sACReg) =>
  await fetch(
    process.env.REACT_APP_SERVER_URL +
    `/getFlt?sFltNo=${sFltNo}&sDep=${sDep}&sDest=${sDest}&sDate=${sDate}&sACReg=${sACReg}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const fetchCIF = async (sFltNo, sDep, sSTD) =>
  await fetch(
    process.env.REACT_APP_SERVER_URL +
    `/getCIF?sFltNo=${sFltNo}&sDep=${sDep}&sSTD=${sSTD}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const fetchCTOT = async (sCallSign, sDepICAO, sDestICAO, dtSTD) =>
  await fetch(
    process.env.REACT_APP_SERVER_URL +
    `/getCTOT?sCallSign=${sCallSign}&sDepICAO=${sDepICAO}&sDestICAO=${sDestICAO}&dtSTD=${dtSTD}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const fetchCTOTCrew = async (sCallSign, sDepICAO, sDestICAO, dtSTD) =>
  await fetch(
    process.env.REACT_APP_SERVER_URL +
    `/getCTOTCrew?sCallSign=${sCallSign}&sDepICAO=${sDepICAO}&sDestICAO=${sDestICAO}&dtSTD=${dtSTD}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

// Backend answers 5xx when its call to AviationWeather.gov fails (rate limit, timeout etc.)
// silent: background fetches the user is not looking at should not show the alert.
const fetchAviationWeather = async (path, silent = false) => {
  const res = await fetch(process.env.REACT_APP_SERVER_URL + path, {
    credentials: 'include',
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (res.status >= 500 && !silent) window.dispatchEvent(new Event(AVIATIONWEATHER_ERROR));
  return res;
};

// Legacy: Fetches TAF in XML format
export const fetchTAF = (ICAOs) =>
  fetchAviationWeather(`/getTAF?ICAOs=${ICAOs}`);

// V2: Fetches TAF in JSON format
export const fetchTAFV2 = (ICAOs, silent) =>
  fetchAviationWeather(`/getTAFV2?ICAOs=${ICAOs}`, silent);

export const fetchMETAR = (ICAOs, silent) =>
  fetchAviationWeather(`/getMETAR?ICAOs=${ICAOs}`, silent);

export const fetchNOTAM = async (ICAOs) =>
  await fetch(process.env.REACT_APP_SERVER_URL + `/getNOTAM?ICAOs=${ICAOs}`, {
    credentials: 'include',
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

export const fetchFltInfoCrew = async (sFltNo, sDep, sDest, sSTD) =>
  await fetch(
    process.env.REACT_APP_SERVER_URL +
    `/getFltInfoCrew?sFltNo=${sFltNo}&sDep=${sDep}&sDest=${sDest}&sSTD=${sSTD}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const fetchSigmets = () => fetchAviationWeather('/getSigmets');

export const fetchCoordinatesFromICAO = (ICAOs) =>
  fetchAviationWeather(`/getCoordinatesFromICAO?ICAO=${ICAOs}`);

export const fetchSNOWTAM = async (ICAOs) =>
  await fetch(process.env.REACT_APP_SERVER_URL + `/getSNOWTAM?ICAOs=${ICAOs}`, {
    credentials: 'include',
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

export const fetchAreaMessages = async () =>
  await fetch(process.env.REACT_APP_SERVER_URL + `/getAreaMessages`, {
    credentials: 'include',
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

export const fetchPreFlightBriefing = async (nBriefingId) => {
  const id = Number.isInteger(nBriefingId) ? nBriefingId : -1;
  return fetch(
    process.env.REACT_APP_SERVER_URL + `/getPreFlightBriefing?nBriefingId=${id}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
};

export const getFlts = async (handleResponseError) => {
  let allFlights = null;

  try {
    const simulatorMode = isTrainingMode();
    const res = await fetchFlts(simulatorMode);
    if (!res.ok) {
      handleResponseError(res);
    }
    const result = await res.json();

    parseString(result, async function (err, result) {
      const data =
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.getFltsResponse?.['0']
          ?.getFltsResult?.['0'];
      if (data === '') {
        allFlights = [];
      }
      if (data) {
        const filteredFlights = filterFlights(data);
        allFlights = generateFlightList(filteredFlights);
      }
    });
  } catch (err) {
    console.error(err);
  }

  return allFlights;
};

export const getFlt = async (flight, handleResponseError) => {
  let flightData = null;

  const sFltNo = flight?.sFltNo?.[0];
  const sDep = flight?.sDep?.[0];
  const sDest = flight?.sDest?.[0];
  const sDate = (flight?.sSTD?.[0] || '').split(' ')[0];
  const sACReg = flight?.sACReg?.[0];

  try {
    const res = await fetchFlt(sFltNo, sDep, sDest, sDate, sACReg);
    if (!res.ok) {
      handleResponseError(res);
    }
    const response = await res.json();

    parseString(response, function (err, result) {
      parseString(
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.getFlt_xmlResponse?.[
          '0'
        ]?.getFlt_xmlResult?.['0'],
        async function (err, data) {
          if (data) {
            let airports = generateAirportList(data.FlightPlanObj);

            const airportSelected =
              data.FlightPlanObj?.Airport_List?.[0]?.AirportData?.[0];

            const reqFuel = calculateReqFuel(
              data.FlightPlanObj?.nBlockFuel_KG,
              data.FlightPlanObj?.Airport_List?.[0]?.AirportData?.[0],
              airportSelected
            );

            flightData = {
              allData: data.FlightPlanObj,
              airports,
              airportSelected,
              reqFuel,
            };
          }
        }
      );
    });
  } catch (err) {
    console.error(err);
  }

  return flightData;
};

export const getSNOWTAM = async (airportCodes, handleResponseError) => {
  let snowtam = null;
  try {
    const res = await fetchSNOWTAM(airportCodes);
    if (!res.ok) {
      handleResponseError(res);
    }
    const response = await res.json();

    parseString(response, async function (err, result) {
      const data =
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.GetSnowtamResponse?.[
          '0'
        ]?.GetSnowtamResult?.['0']?.SnowtamResp;

      if (data) {
        snowtam = data;
      }
    });
  } catch (err) {
    console.error(err);
  }

  return snowtam;
};

export const getMETAR = async (ICAOs, silent) => {
  let metar = null;
  try {
    await fetchMETAR(ICAOs, silent)
      .then((res) => res.json())
      .then((res) => {
        metar = res?.[0]?.METAR ? res[0].METAR : null;
      });
  } catch (err) {
    console.error(`Could not get METAR for ICAOs ${ICAOs}`, { err });
  }

  return metar || [];
};

// V2: Returns TAF in JSON format {icaoId, rawTAF}
export const getTAFV2 = async (ICAOs, silent) => {
  let taf = null;
  try {
    await fetchTAFV2(ICAOs, silent)
      .then((res) => res.json())
      .then((res) => {
        taf = Array.isArray(res) ? res : null;
      });
  } catch (err) {
    console.error(`Could not get TAF for ICAO ${ICAOs}`, { err });
  }

  return taf || [];
};

// Legacy: Returns TAF in old XML-compatible format {station_id: [icao], raw_text: [taf]}
// Deprecated - use getTAFV2 instead
export const getTAF = async (ICAOs) => {
  let taf = null;
  try {
    await fetchTAF(ICAOs)
      .then((res) => res.json())
      .then((res) => {
        // Backend returns XML-parsed format
        taf = res;
      });
  } catch (err) {
    console.error(`Could not get TAF for ICAO ${ICAOs}`, { err });
  }

  return taf || [];
};

export const getCTOT = async (allData, handleResponseError) => {
  let ctot = null;

  const sCallSign = allData?.sCallSign?.[0];
  const sDepICAO = allData?.sDepICAO?.[0];
  const sDestICAO = allData?.sDestICAO?.[0];
  const dtSTD = allData?.dtSTD?.[0];

  try {
    const res = await fetchCTOT(sCallSign, sDepICAO, sDestICAO, dtSTD);
    if (!res.ok) {
      handleResponseError(res);
    }
    const response = await res.json();

    parseString(response, async function (err, result) {
      const data =
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.GetCTOTResponse?.['0']
          ?.GetCTOTResult?.['0']?.CTOT?.['0'];

      if (data) {
        ctot = data;
      }
    });
  } catch (err) {
    console.error(err);
  }

  return ctot;
};

export const getCTOTCrew = async (allData, handleResponseError) => {
  let ctotCrew = null;

  const sCallSign = allData?.sCallSign?.[0];
  const sDepICAO = allData?.sDepICAO?.[0];
  const sDestICAO = allData?.sDestICAO?.[0];
  const dtSTD = allData?.dtSTD?.[0];

  try {
    const res = await fetchCTOTCrew(sCallSign, sDepICAO, sDestICAO, dtSTD);
    if (!res.ok) {
      handleResponseError(res);
    }
    const response = await res.json();

    parseString(response, async function (err, result) {
      const data =
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.GetCTOT_CrewResponse?.['0']
          ?.GetCTOT_CrewResult?.['0'];

      if (data) {
        ctotCrew = {
          ctot: data.CTOT?.[0] || null,
          taxiTime: data.TaxiTime?.[0] || null,
          ctotMsg: data.CTOT_Msg?.[0] || null,
          errorMsg: data.ErrorMsg?.[0] || null,
        };
      }
    });
  } catch (err) {
    console.error(err);
  }

  return ctotCrew;
};

export const getCIF = async (flight, handleResponseError) => {
  let cif = null;

  const sFltNo = flight?.sFltNo?.[0];
  const sDep = flight?.sDep?.[0];
  const sSTD = flight?.sSTD?.[0];

  try {
    const res = await fetchCIF(sFltNo, sDep, sSTD);
    if (!res.ok) {
      handleResponseError(res);
    }

    const response = await res.json();

    parseString(response, async function (err, result) {
      const data =
        result?.['soap:Envelope']?.['soap:Body']?.['0']?.getCIFResponse?.['0']
          ?.getCIFResult?.['0'];
      if (data) {
        cif = data;
      }
    });
  } catch (err) {
    console.error(err);
  }

  return cif;
};

export const getFltInfoCrew = async (flight, handleResponseError) => {
  let flightInfoCrew = null;

  const sFltNo = flight?.sFltNo?.[0];
  const sDep = flight?.sDep?.[0];
  const sDest = flight?.sDest?.[0];
  const sSTD = flight?.sSTD?.[0];

  try {
    const res = await fetchFltInfoCrew(sFltNo, sDep, sDest, sSTD);
    if (!res.ok) {
      handleResponseError(res);
    }

    const response = await res.json();

    parseString(response, async function (err, result) {
      const dataResult =
        result?.['soap:Envelope']?.['soap:Body']?.['0']
          ?.getFltInfoCrewResponse?.['0']?.getFltInfoCrewResult?.['0'];
      if (dataResult) {
        flightInfoCrew = dataResult;
      }
    });
  } catch (err) {
    console.error(err);
  }

  return flightInfoCrew;
};

export const getPreFlightBriefing = async (nBriefingId, handleResponseError) => {
  try {
    const res = await fetchPreFlightBriefing(nBriefingId);
    if (!res.ok) {
      handleResponseError(res);
      return null;
    }

    const response = await res.json();
    const result = await parseStringPromise(response);
    const node =
      result?.['soap:Envelope']?.['soap:Body']?.[0]
        ?.GetPreFlightBriefingResponse?.[0];
    const serverIdRaw = node?.briefingIdActive?.[0] ?? node?.nServerId?.[0];
    if (!node || serverIdRaw === undefined || serverIdRaw === '') return null;

    const serverId = Number(serverIdRaw);
    if (!Number.isInteger(serverId)) return null;

    const body = node.GetPreFlightBriefingResult?.[0];
    return {
      body: decodeBriefingBody(typeof body === 'string' ? body : ''),
      serverId,
      errorMsg: node.sErrorMsg?.[0] ?? '',
    };
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const getAreaMessages = async (handleResponseError) => {
  let areaMessages = null;

  try {
    const res = await fetchAreaMessages();
    if (!res.ok) {
      handleResponseError(res);
    }

    const response = await res.json();

    parseString(response, async function (err, result) {
      const dataResult =
        result?.['soap:Envelope']?.['soap:Body']?.['0']
          ?.GetAreaMessagesResponse?.['0']?.GetAreaMessagesResult?.[0]
          ?.AreaMessage;
      if (dataResult) {
        areaMessages = dataResult;
      }
    });
  } catch (err) {
    console.error(err);
  }

  return areaMessages;
};

const convertTrainingADToNOTAM = (trainingADList) => {
  if (!trainingADList || !Array.isArray(trainingADList)) {
    return [];
  }

  return trainingADList.map((trainingAD) => ({
    sICAO: trainingAD.sICAO || [],
    sIATA: trainingAD.sIATA || [],
    sAirportName: trainingAD.sAirportName || [],
    sAirportFunction: trainingAD.sAirportFunction || [],
    sNOTAM: trainingAD.sNOTAM || [],
    sWX: trainingAD.sWX || [],
    sRAIM: trainingAD.sRAIM || [],
    sSpaceWeather: trainingAD.sSpaceWeather || [],
  }));
}

export const getTrainingWXFromAllData = (allData, icao) => {
  const trainingMode = isTrainingMode();

  if (!trainingMode) {
    return null;
  }

  let trainingData = null;

  if (Array.isArray(allData?.training) && allData.training.length > 0) {
    trainingData = allData.training[0];
  } else if (allData?.adList) {
    trainingData = Array.isArray(allData.adList) ? allData.adList[0] : allData.adList;
  }

  if (trainingData?.adList) {
    const adList = Array.isArray(trainingData.adList) ? trainingData.adList : [trainingData.adList];
    for (const adListItem of adList) {
      if (adListItem?.TrainingAD && Array.isArray(adListItem.TrainingAD)) {
        const trainingAD = adListItem.TrainingAD.find(
          (ad) => ad?.sICAO?.[0] === String(icao).toUpperCase()
        );
        if (trainingAD?.sWX) {
          return trainingAD;
        }
      }
    }
  }

  return null;
}

export const getTrainingSnowtamFromAllData = (allData, icao) => {
  const trainingMode = isTrainingMode();

  if (!trainingMode || !icao) {
    return null;
  }

  let trainingData = null;

  if (Array.isArray(allData?.training) && allData.training.length > 0) {
    trainingData = allData.training[0];
  } else if (allData?.adList) {
    trainingData = Array.isArray(allData.adList) ? allData.adList[0] : allData.adList;
  }

  const snowtamList =
    trainingData?.SnowtamList_Training?.[0]?.Snowtam ??
    trainingData?.SnowtamList_Training?.Snowtam;

  if (!snowtamList) {
    return null;
  }

  const entries = Array.isArray(snowtamList) ? snowtamList : [snowtamList];
  const icaoUpper = String(icao).toUpperCase();
  const entry = entries.find((s) => {
    const entryIcao = Array.isArray(s?.ICAO) ? s.ICAO[0] : s?.ICAO;
    return String(entryIcao || '').toUpperCase() === icaoUpper;
  });

  if (!entry?.SnowtamText) {
    return null;
  }

  const text = Array.isArray(entry.SnowtamText)
    ? entry.SnowtamText[0]
    : entry.SnowtamText;

  if (!text || typeof text !== 'string') {
    return null;
  }

  return {
    sICAO: [icaoUpper],
    sSnowtam: text,
  };
};

export const parseTrainingWX = (sWX) => {
  const wxString = Array.isArray(sWX) ? sWX[0] : sWX;
  if (!wxString || typeof wxString !== 'string') {
    return { metar: '', taf: '' };
  }

  let metar = '';
  let taf = '';

  const saMatch = wxString.match(/SA\s+([\s\S]*?)(?=\n\s+FT\s+|$)/);
  if (saMatch) {
    metar = saMatch[1]
      .trim()
      .replace(/=\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const ftMatch = wxString.match(/(?:^|\n)\s*FT\s+([\s\S]*)/);
  if (ftMatch) {
    taf = ftMatch[1]
      .trim()
      .replace(/=\s*$/, '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join(' ')
      .trim();
  }

  return { metar, taf };
}

export const getNOTAMFromAllData = (allData, icao) => {
  const trainingMode = isTrainingMode();

  if (trainingMode) {
    let trainingData = null;

    if (Array.isArray(allData?.training) && allData.training.length > 0) {
      trainingData = allData.training[0];
    } else if (allData?.adList) {
      trainingData = Array.isArray(allData.adList) ? allData.adList[0] : allData.adList;
    }

    if (trainingData?.adList) {
      const adList = Array.isArray(trainingData.adList) ? trainingData.adList : [trainingData.adList];
      for (const adListItem of adList) {
        if (adListItem?.TrainingAD && Array.isArray(adListItem.TrainingAD)) {
          const trainingNOTAMList = convertTrainingADToNOTAM(adListItem.TrainingAD);
          const notam = getNOTAM(trainingNOTAMList, icao);
          if (notam) {
            return notam;
          }
        }
      }
    }
    return null;
  } else {
    if (allData?.NOTAM_List?.[0]?.NOTAM) {
      return getNOTAM(allData.NOTAM_List[0].NOTAM, icao);
    }
    return null;
  }
}

export const getNOTAMListFromAllData = (allData) => {
  const trainingMode = isTrainingMode();

  if (trainingMode) {
    let trainingData = null;

    if (Array.isArray(allData?.training) && allData.training.length > 0) {
      trainingData = allData.training[0];
    } else if (allData?.adList) {
      trainingData = Array.isArray(allData.adList) ? allData.adList[0] : allData.adList;
    }

    if (trainingData?.adList) {
      const adList = Array.isArray(trainingData.adList) ? trainingData.adList : [trainingData.adList];
      for (const adListItem of adList) {
        if (adListItem?.TrainingAD && Array.isArray(adListItem.TrainingAD)) {
          return convertTrainingADToNOTAM(adListItem.TrainingAD);
        }
      }
    }
    return [];
  } else {
    if (allData?.NOTAM_List?.[0]?.NOTAM) {
      return allData.NOTAM_List[0].NOTAM;
    }
    return [];
  }
}

export function getNOTAM(notamList, icao) {
  return (notamList || []).find(
    (item) => item?.sICAO?.[0] === String(icao).toUpperCase()
  );
}

export function getFlightPlanAirportIcaos(allData) {
  const icaos = new Set();

  const airportDataList = allData?.Airport_List?.[0]?.AirportData || [];
  for (const airport of airportDataList) {
    const functionType = airport?.sAirportFunction?.[0];
    if (
      functionType === 'DepartureAirport' ||
      functionType === 'ArrivalAirport'
    ) {
      const icao = airport?.sICAO?.[0];
      if (icao) {
        icaos.add(icao.toUpperCase());
      }
    }
  }

  const alternates = allData?.Alt_List?.[0]?.Alternate || [];
  for (const alternate of alternates) {
    const icao = alternate?.sADICAO?.[0];
    if (icao) {
      icaos.add(icao.toUpperCase());
    }
  }

  return icaos;
}

export function isFlightPlanAirport(icao, allData) {
  if (!icao) {
    return false;
  }
  return getFlightPlanAirportIcaos(allData).has(String(icao).toUpperCase());
}

export function getLidoNotamText(allData, icao) {
  const notamObj = getNOTAMFromAllData(allData, icao);
  if (!notamObj?.sNOTAM) {
    return null;
  }

  return Array.isArray(notamObj.sNOTAM)
    ? notamObj.sNOTAM[0]
    : notamObj.sNOTAM;
}

export function stripFaaNotamFromResponse(notamData) {
  if (!notamData) {
    return notamData;
  }

  return { ...notamData, sNOTAM: '', NOTAM: [], errorMsg: '' };
}

export function hoursToMinutes(hours) {
  const sanitizedHours = hours || 0;

  return Math.round(Math.abs(sanitizedHours) * 60);
}

export const minutesToHoursAndMinutes = (minutes) => {
  const sanitizedMinutes = minutes || 0;

  const hours = Math.floor(sanitizedMinutes / 60);
  const min = sanitizedMinutes - hours * 60;
  return hours + ':' + (min < 10 ? '0' : '') + min;
};

export function timeConvert(hours) {
  const sanitizedHours = hours || 0;

  var sign = sanitizedHours < 0 ? '-' : '';
  var h = Math.floor(Math.abs(sanitizedHours));
  var min = Math.floor((Math.abs(sanitizedHours) * 60) % 60);
  return sign + h + ':' + (min < 10 ? '0' : '') + min;
}

export function timeConvert_Floor(hours) {
  const sanitizedHours = hours || 0;

  var h = Math.floor(Math.abs(sanitizedHours));
  var min = Math.floor((Math.abs(sanitizedHours) * 60) % 60);
  return (h < 10 ? '0' : '') + h + ':' + (min < 10 ? '0' : '') + min;
}

// PGD 2019-12-09
export function timeConvert_Ceil(hours) {
  const sanitizedHours = hours || 0;

  var sign = sanitizedHours < 0 ? '-' : '';
  var h = Math.floor(Math.abs(sanitizedHours));
  var min = Math.ceil((Math.abs(sanitizedHours) * 60) % 60);
  return sign + (h < 10 ? '0' : '') + h + ':' + (min < 10 ? '0' : '') + min;
}

// PGD 2019-12-09
export function timeConvert_Round(hours) {
  const sanitizedHours = hours || 0;

  var sign = sanitizedHours < 0 ? '-' : '';
  var h = Math.floor(Math.abs(sanitizedHours));
  var min = Math.round((Math.abs(sanitizedHours) * 60) % 60);
  return sign + (h < 10 ? '0' : '') + h + ':' + (min < 10 ? '0' : '') + min;
}

// Formats departure time from allData object
export function getDepartureTime(data) {
  let departureTime = data?.dtSTD?.[0] || '';
  departureTime = departureTime.replace(/[\T,\Z]/g, ' ');
  departureTime = departureTime.split(' ')?.[1]?.substring(0, 5);
  return departureTime;
}

// Formats arrival time from allData object
export function getArrivalTime(data) {
  let arrivalTime = data?.dtSTA?.[0] || '';
  arrivalTime = arrivalTime.replace(/[\T,\Z]/g, ' ');
  arrivalTime = arrivalTime.split(' ')?.[1]?.substring(0, 5);
  return arrivalTime;
}

// Formats ACReg from allData object
export function getACReg(data) {
  let ACReg = data?.sACReg?.[0] || '';
  const index = 2;
  ACReg = ACReg.slice(0, index) + '-' + ACReg.slice(index);
  return ACReg;
}

// Generates a list of flight data objects
export function generateFlightList(flightData) {
  let flights = [];
  for (let i = 0; i < flightData.length; i++) {
    const departureDateTime = flightData[i]?.sSTD?.[0]
      ? moment.utc(flightData[i].sSTD[0], 'YYYY-MM-DD HH:mm').toDate()
      : undefined;
    const arrivalDateTime = flightData[i]?.sSTA?.[0]
      ? moment.utc(flightData[i].sSTA[0], 'YYYY-MM-DD HH:mm').toDate()
      : undefined;

    flights.push({
      ...flightData[i],
      departureDateTime,
      arrivalDateTime,
      flightDate: (flightData[i]?.sSTD?.[0] || '').slice(0, 10),
      id: flightData[i]?.sFltId?.[0],
    });
  }
  return flights;
}

// get the amount of mels from the data object, used in top menu
export function getMelAmount(melString) {
  let melArray = (melString || '').split(
    '*****************************************************'
  );
  melArray.pop(); // removes last element of list (last element wont be mel data in the data object)
  return melArray.length;
}

// Separates mel info
export function getMelTextContent(data) {
  let melString = data?.sABC_MEL?.[0] || '';
  let melArray = melString.split(
    '*****************************************************'
  );
  return melArray;
}

// Parses MEL text and matches with deep links
export function getMelItemsWithLinks(data) {
  let melString = data?.sABC_MEL?.[0] || '';
  let melArray = melString.split(
    '*****************************************************'
  );

  const melLinks = {};

  if (data?.MelItem_List) {
    const melItemList = data.MelItem_List[0]?.MelItem || [];
    const items = Array.isArray(melItemList) ? melItemList : [melItemList];

    items.forEach((item) => {
      const ref = item?.sItem?.[0];
      const link = item?.sLink?.[0];
      if (ref && link) {
        melLinks[ref] = link;
      }
    });
  }

  return melArray.map((text) => {
    const match = text.match(/MEL REF\s*:\s*"([^"]*)"/);
    const melRef = match ? match[1] : null;

    return {
      rawText: text,
      melRef: melRef,
      link: melRef ? melLinks[melRef] : null,
    };
  });
}

// Used for the airport lists in the fpl+fuel and weather pages
// Guarantees that the departure and arrival airports are first and second in the airport list
export function generateAirportList(data) {
  let airportList = [];

  for (
    let i = 0;
    i < (data?.Airport_List?.[0]?.AirportData || []).length;
    i++
  ) {
    if (
      data.Airport_List[0].AirportData[i]?.sAirportFunction?.[0] ===
      'DepartureAirport'
    ) {
      airportList.push(data.Airport_List[0].AirportData[i]);
    }
    if (
      data.Airport_List[0].AirportData[i]?.sAirportFunction?.[0] ===
      'ArrivalAirport'
    ) {
      airportList.push(data.Airport_List[0].AirportData[i]);
    }
  }

  airportList.push(
    ...(data.Alt_List && data.Alt_List[0]?.Alternate
      ? data.Alt_List[0].Alternate
      : [])
  );

  return airportList;
}
